import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import {MatSelect, MatSelectChange} from "@angular/material/select";
import {Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, takeUntil} from "rxjs/operators";

export interface SelectTypePaginated
{
	label: String;
	value: any;
}

/** Default number of options rendered per page */
const DEFAULT_PAGE_SIZE = 20;

/**
 * Select with client-side pagination of the options list.
 *
 * Only one page of options (default 20) is kept in the DOM at any time, which keeps
 * rendering fast on very large lists. The currently selected option is always kept
 * in the DOM (hidden when not on the current page) so MatSelect can render its label.
 */
@Component({
	selector: 'lc-custom-select-paginated',
	templateUrl: './lc-custom-select-paginated.component.html',
	styleUrls: ['./lc-custom-select-paginated.component.scss']
})
export class LcCustomSelectPaginatedComponent implements OnInit, OnChanges, OnDestroy
{
	@Input()
	label: String | undefined;

	@Input()
	placeholder: String | undefined;

	@Input()
	values?: any[];

	@Input()
	defaultValue: any;

	@Input()
	disabled: boolean | undefined = false;

	@Output()
	defaultValueChange: EventEmitter<any> = new EventEmitter<any>();

	@Input()
	columnName?: string;

	@Output()
	columnChange: EventEmitter<string> = new EventEmitter<string>();

	@Input()
	extraClass: string = '';

	/** When true the local defaultValue keeps the raw id instead of the whole option object. */
	@Input()
	preventIdChange: boolean = false;

	@Input()
	searchable: boolean = true;

	@Input()
	useTranslation: boolean = false;

	@Input()
	translationPrefix: string = "";

	/** Number of options rendered per page. */
	@Input()
	pageSize: number = DEFAULT_PAGE_SIZE;

	/** When true, auto-selects the first available value if none is set. */
	@Input()
	autoSelectFirst: boolean = true;

	@ViewChild('selectElement') selectElement: MatSelect | undefined;
	@ViewChild('searchInput') searchInput: ElementRef | undefined;

	/**
	 * Id bound to the inner mat-select.
	 * It is kept separate from [defaultValue] because the latter may be replaced with the
	 * whole option object when [preventIdChange] is false.
	 */
	selectedId: any = null;

	/** Current search input value */
	searchValue: string = '';

	/** Filtered and paginated options actually rendered */
	renderedOptions: any[] = [];

	/** Selected option not in current page (kept in DOM so MatSelect can show its label) */
	hiddenSelectedOption: any | null = null;

	/** Total count of filtered options (before pagination) */
	totalFilteredCount: number = 0;

	/** Current page (1-based) */
	currentPage: number = 1;

	/** Total number of pages */
	totalPages: number = 1;

	/** Cached filtered options used for pagination */
	private currentFilteredOptions: any[] = [];

	/** Subject for search input with debounce */
	private readonly searchSubject = new Subject<string>();

	/** Subject for component destruction cleanup */
	private readonly destroy$ = new Subject<void>();

	constructor()
	{
		this.setupSearchDebounce();
	}

	ngOnInit(): void
	{
		if (this.autoSelectFirst && this.isEmptyValue(this.getSelectedId()) && this.values?.length)
		{
			this.defaultValue = this.values[0].id;
			this.defaultValueChange.emit(this.values[0].id);
			this.columnChange.emit(this.columnName ?? '');
		}

		this.selectedId = this.getSelectedId();
		this.currentPage = 1;
		this.applyFilter(this.searchValue);
	}

	ngOnChanges(changes: SimpleChanges): void
	{
		if (changes['defaultValue'])
		{
			this.selectedId = this.getSelectedId();
		}

		if ((changes['values'] && Array.isArray(changes['values'].currentValue)) || changes['pageSize'])
		{
			this.currentPage = 1;
			this.applyFilter(this.searchValue);
		}
		else if (changes['defaultValue'])
		{
			//Update the hidden option when the selected value changes from the parent
			this.updateRenderedOptions();
		}
	}

	ngOnDestroy(): void
	{
		this.destroy$.next();
		this.destroy$.complete();
		this.searchSubject.complete();
	}

	/**
	 * Sets up debounced search to avoid filtering on every keystroke
	 */
	private setupSearchDebounce(): void
	{
		this.searchSubject.pipe(
			debounceTime(150),
			distinctUntilChanged(),
			takeUntil(this.destroy$)
		).subscribe(searchTerm =>
		{
			this.currentPage = 1;
			this.applyFilter(searchTerm);
		});
	}

	/**
	 * Applies the filter to the options and recalculates pagination.
	 */
	private applyFilter(searchTerm: string): void
	{
		const normalizedTerm = (searchTerm || '').trim().toUpperCase();

		if (!normalizedTerm)
		{
			this.currentFilteredOptions = this.values ? [...this.values] : [];
		}
		else
		{
			this.currentFilteredOptions = (this.values || []).filter(option => this.matchesSearch(option, normalizedTerm));
		}

		this.totalFilteredCount = this.currentFilteredOptions.length;
		this.totalPages = Math.max(1, Math.ceil(this.totalFilteredCount / this.effectivePageSize));

		if (this.currentPage > this.totalPages)
		{
			this.currentPage = this.totalPages;
		}

		this.updateRenderedOptions();
	}

	/**
	 * Updates the rendered options based on the current page.
	 * Keeps the selected option in the DOM (hidden) when it is not on the current page,
	 * so that MatSelect can still display its label.
	 */
	private updateRenderedOptions(): void
	{
		const startIndex = (this.currentPage - 1) * this.effectivePageSize;
		const endIndex = startIndex + this.effectivePageSize;
		this.renderedOptions = this.currentFilteredOptions.slice(startIndex, endIndex);

		const selectedId = this.selectedId;
		const isRendered = this.renderedOptions.some(option => option?.id == selectedId);

		if (!this.isEmptyValue(selectedId) && !isRendered)
		{
			this.hiddenSelectedOption = (this.values || []).find(option => option?.id == selectedId) || null;
		}
		else
		{
			this.hiddenSelectedOption = null;
		}
	}

	/** Navigate to first page */
	goToFirstPage(event: MouseEvent): void
	{
		event.stopPropagation();
		if (this.currentPage !== 1)
		{
			this.currentPage = 1;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
		}
	}

	/** Navigate to previous page */
	goToPreviousPage(event: MouseEvent): void
	{
		event.stopPropagation();
		if (this.currentPage > 1)
		{
			this.currentPage--;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
		}
	}

	/** Navigate to next page */
	goToNextPage(event: MouseEvent): void
	{
		event.stopPropagation();
		if (this.currentPage < this.totalPages)
		{
			this.currentPage++;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
		}
	}

	/** Navigate to last page */
	goToLastPage(event: MouseEvent): void
	{
		event.stopPropagation();
		if (this.currentPage !== this.totalPages)
		{
			this.currentPage = this.totalPages;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
		}
	}

	/** Handles search input changes (debounced) */
	onSearchValueChange(event: any): void
	{
		this.searchValue = event || '';
		this.searchSubject.next(this.searchValue);
	}

	/** Clears the search input and resets the filter */
	clearSearch(): void
	{
		this.searchValue = '';
		this.searchSubject.next('');
	}

	onChange(event: MatSelectChange): void
	{
		const value = event.value;
		this.selectedId = value;

		if (this.preventIdChange || !this.values)
		{
			this.defaultValue = value;
		}
		else
		{
			this.defaultValue = this.values.find(option => option?.id == value);
		}

		//Update the hidden option visibility after the selection
		this.updateRenderedOptions();
		this.defaultValueChange.emit(value);
		this.columnChange.emit(this.columnName ?? '');
	}

	openedChange(isOpen: boolean): void
	{
		if (isOpen)
		{
			this.currentPage = 1;
			this.applyFilter(this.searchValue);
			this.scrollPanelToTop();
			setTimeout(() =>
			{
				this.searchInput?.nativeElement?.focus();
			});
		}
		else if (this.searchValue)
		{
			this.clearSearch();
		}
	}

	/** TrackBy function for ngFor optimization */
	trackByOptionId(index: number, option: any): any
	{
		return option?.id ?? index;
	}

	/** Weak equality, so that ids of different types (string/number) still match */
	compare(x: any, y: any): boolean
	{
		return x == y;
	}

	private get effectivePageSize(): number
	{
		return this.pageSize && this.pageSize > 0 ? this.pageSize : DEFAULT_PAGE_SIZE;
	}

	/** defaultValue may hold either the raw id or the whole option object (see preventIdChange) */
	private getSelectedId(): any
	{
		if (this.defaultValue !== null && typeof this.defaultValue === 'object')
		{
			return this.defaultValue.id;
		}
		return this.defaultValue;
	}

	private scrollPanelToTop(): void
	{
		setTimeout(() =>
		{
			const panel = this.selectElement?.panel?.nativeElement as HTMLElement;
			if (panel)
			{
				panel.scrollTop = 0;
			}
		});
	}

	private isEmptyValue(value: any): boolean
	{
		return value === null || value === undefined || value === '';
	}

	private matchesSearch(option: any, upperCasedTerm: string): boolean
	{
		const id = String(option?.id ?? '').toUpperCase();
		const name = String(option?.name ?? '').toUpperCase();
		return id.includes(upperCasedTerm) || name.includes(upperCasedTerm);
	}
}
